import { Paperclip } from "@ignite-analytics/icons";
import { Box, Chip, Collapse, Link, Stack, TextField, Typography } from "@mui/material";
import React, { useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAlert } from "../lib/Alerts";

import { type Answer, type AnswerUpdate, type InputProps, type QuestionType, followUpLevel, isEmpty } from "./answers";
import { useCodeOfConductSource } from "./codeOfConduct";
import type { Question as QuestionDef } from "./contents";
import { useDocumentTypes } from "./documentTypes";
import { useFileActions } from "./fileActions";
import { FilePicker } from "./FilePicker";
import type { Review } from "./prefill";
import { QUESTION_INPUTS } from "./questionTypes";
import { fieldSx } from "./questionTypes/optionSx";
import { SEARCHABLE_FROM } from "./questionTypes/singleSelect";
import { RichText } from "./RichText";
import { SuggestionCard } from "./SuggestionCard";
import type { Problem } from "./validation";

type Props = {
    question: QuestionDef;
    number: string;
    answer: Answer | undefined;
    onChange: (answer: AnswerUpdate) => void;
    problems: Problem["kind"][];
    review?: Review;
    onConfirm?: () => void;
    onReject?: () => void;
};

const ProblemText: React.FC<{ kind: Problem["kind"] }> = ({ kind }) => {
    switch (kind) {
        case "required":
            return <FormattedMessage defaultMessage="This question needs an answer." />;
        case "tooLong":
            return <FormattedMessage defaultMessage="The answer is too long." />;
        case "format":
            return <FormattedMessage defaultMessage="The answer is not in the expected format." />;
        case "outOfRange":
            return <FormattedMessage defaultMessage="The number is outside the allowed range." />;
        case "other":
            return <FormattedMessage defaultMessage="Please specify your other answer." />;
        case "comment":
            return <FormattedMessage defaultMessage="A comment is required for this answer." />;
        case "file":
            return <FormattedMessage defaultMessage="A file is required for this answer." />;
        case "expiry":
            return <FormattedMessage defaultMessage="Please add an expiry date." />;
    }
};

export const Question: React.FC<Props> = ({
    question,
    number,
    answer,
    onChange,
    problems,
    review,
    onConfirm,
    onReject,
}) => {
    const { formatMessage, formatList } = useIntl();
    const documentTypes = useDocumentTypes();
    const { customerName } = useCodeOfConductSource();
    const fileActions = useFileActions();
    const { alertUser } = useAlert();
    const openAttachment = (path: string) =>
        fileActions?.openAttachment(path).catch(() =>
            alertUser({
                value: formatMessage({ defaultMessage: "Could not download the file. Please try again." }),
                severity: "error",
            })
        );
    const title =
        question.title ||
        (question.type === "signature"
            ? customerName
                ? formatMessage({ defaultMessage: "Sign {customerName}'s supplier code of conduct" }, { customerName })
                : formatMessage({ defaultMessage: "Sign the supplier code of conduct" })
            : "") ||
        formatList(
            (question.type === "file" ? (question.documentTypeIds ?? []) : []).map(
                (id) => documentTypes.find((type) => type.id === id)?.name ?? id
            ),
            { type: "disjunction" }
        );
    const Input = QUESTION_INPUTS[question.type] as React.FC<InputProps<QuestionType>>;
    const comment = followUpLevel(question, answer, "comment");
    const file = followUpLevel(question, answer, "file");
    const commentRef = useRef<HTMLTextAreaElement>(null);
    const clearable =
        (question.type === "boolean" ||
            (question.type === "singleSelect" && question.options.length <= SEARCHABLE_FROM)) &&
        !isEmpty(answer?.value);

    return (
        <Box
            id={`question-${question.id}`}
            sx={{
                position: "relative",
                scrollMarginTop: 96,
                "&:hover .clear-answer, &:focus-within .clear-answer": { opacity: 1 },
            }}
        >
            <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                <Stack spacing={1}>
                    <Stack direction="row" spacing={2} sx={{ alignItems: "baseline" }}>
                        <Typography variant="textMd" fontWeight={500}>
                            <FormattedMessage defaultMessage="{number} {title}" values={{ number, title }} />
                        </Typography>
                        {question.required && (
                            <Typography
                                variant="textXs"
                                color="textTextHelper"
                                sx={{ textTransform: "uppercase", letterSpacing: "0.04em", flexShrink: 0 }}
                            >
                                <FormattedMessage defaultMessage="Required" />
                            </Typography>
                        )}
                        {clearable && (
                            <Link
                                className="clear-answer"
                                component="button"
                                type="button"
                                variant="textXs"
                                color="textTextHelper"
                                underline="hover"
                                onClick={() =>
                                    onChange((current) => ({ ...current, value: undefined, other: undefined }))
                                }
                                sx={{
                                    ml: "auto !important",
                                    flexShrink: 0,
                                    opacity: 0,
                                    "@media (hover: none)": { opacity: 1 },
                                }}
                            >
                                <FormattedMessage defaultMessage="Clear" />
                            </Link>
                        )}
                    </Stack>
                    {question.description && <RichText doc={question.description} />}
                    {!!question.attachments?.length && (
                        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
                            {question.attachments.map((attachment) => (
                                <Chip
                                    key={attachment.id}
                                    size="small"
                                    icon={<Paperclip fontSize="small" />}
                                    label={attachment.name}
                                    onClick={fileActions ? () => openAttachment(attachment.path) : undefined}
                                />
                            ))}
                        </Stack>
                    )}
                </Stack>
                <Input
                    question={question}
                    answer={answer}
                    onChange={onChange}
                    error={problems.some((kind) => kind !== "comment" && kind !== "file")}
                />
                <Collapse
                    in={comment !== "off"}
                    unmountOnExit
                    onEntered={() => commentRef.current?.focus()}
                    sx={{ "&&": { mt: 0 }, "& .MuiCollapse-wrapperInner": { pt: 2 } }}
                >
                    <Stack spacing={1}>
                        <Typography variant="textSm" color="textTextSecondary">
                            {comment === "required" ? (
                                <FormattedMessage defaultMessage="Comment (required)" />
                            ) : (
                                <FormattedMessage defaultMessage="Comment (optional)" />
                            )}
                        </Typography>
                        <TextField
                            inputRef={commentRef}
                            multiline
                            minRows={2}
                            size="small"
                            placeholder={formatMessage({ defaultMessage: "Add a comment" })}
                            value={answer?.comment ?? ""}
                            error={problems.includes("comment")}
                            onChange={(event) => onChange({ ...answer, comment: event.target.value })}
                            sx={fieldSx}
                        />
                    </Stack>
                </Collapse>
                <Collapse
                    in={file !== "off"}
                    unmountOnExit
                    sx={{ "&&": { mt: 0 }, "& .MuiCollapse-wrapperInner": { pt: 2 } }}
                >
                    <Stack spacing={1}>
                        <Typography variant="textSm" color="textTextSecondary">
                            {file === "required" ? (
                                <FormattedMessage defaultMessage="Supporting documents (required)" />
                            ) : (
                                <FormattedMessage defaultMessage="Supporting documents (optional)" />
                            )}
                        </Typography>
                        <FilePicker
                            questionKey={question.id}
                            answer={answer}
                            multi
                            files={answer?.files ?? []}
                            error={problems.includes("file")}
                            onChange={(update) =>
                                onChange((current) => ({ ...current, files: update(current?.files ?? []) }))
                            }
                        />
                    </Stack>
                </Collapse>
                {problems.map((kind) => (
                    <Typography key={kind} variant="textSm" color="textTextError">
                        <ProblemText kind={kind} />
                    </Typography>
                ))}
            </Stack>
            {review && onConfirm && onReject && (
                <Box
                    sx={{
                        mt: { xs: 2, lg: 0 },
                        position: { lg: "absolute" },
                        top: 0,
                        left: "calc(100% + 49px)",
                        width: { lg: 260 },
                    }}
                >
                    <SuggestionCard question={question} review={review} onConfirm={onConfirm} onReject={onReject} />
                </Box>
            )}
        </Box>
    );
};
